import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calculator, 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Download,
  RefreshCw,
  BarChart3,
  FileText,
  DollarSign,
  Percent
} from 'lucide-react';
import { 
  calculateProductAMP, 
  generateAMPReport, 
  getAMPHistory,
  getProduct,
  exportAMPCalculations
} from '../../config/api';

const AMPCalculation = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [calculationOptions, setCalculationOptions] = useState({
    applyMedicaidAdjustment: true,
    apply340BAdjustment: true,
    applyPromptPayDiscount: true,
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    customAdjustments: []
  });

  // Fetch product details
  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProduct(productId)
  });

  // AMP calculation mutation
  const calculationMutation = useMutation({
    mutationFn: (options) => calculateProductAMP(productId, options),
    onSuccess: (data) => {
      console.log('AMP calculation completed:', data);
    },
    onError: (error) => {
      console.error('AMP calculation failed:', error);
    }
  });

  // AMP report generation mutation
  const reportMutation = useMutation({
    mutationFn: (options) => generateAMPReport(productId, options),
    onSuccess: (data) => {
      console.log('AMP report generated:', data);
    },
    onError: (error) => {
      console.error('AMP report generation failed:', error);
    }
  });

  // AMP history query
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['ampHistory', productId],
    queryFn: () => getAMPHistory(productId, { limit: 10 }),
    enabled: !!productId
  });

  const handleCalculate = () => {
    calculationMutation.mutate(calculationOptions);
  };

  const handleGenerateReport = () => {
    reportMutation.mutate(calculationOptions);
  };

  const handleExport = () => {
    exportAMPCalculations([productId], calculationOptions, 'csv');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getComplianceColor = (compliant) => {
    return compliant ? 'text-green-600' : 'text-red-600';
  };

  const getComplianceIcon = (compliant) => {
    return compliant ? CheckCircle : AlertTriangle;
  };

  if (productLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <button
            onClick={() => navigate('/app/products')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            title="Back to Products"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const calculationResult = calculationMutation.data?.data;
  const reportResult = reportMutation.data?.data;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/app/products')}
            className="p-2 text-gray-500 hover:text-gray-700"
            title="Back to Products"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AMP Calculation</h1>
            <p className="text-gray-600">{product.name}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">NDC:</span>
              <span className="ml-2 text-gray-900">{product.ndc11}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">WAC:</span>
              <span className="ml-2 text-gray-900">{formatCurrency(product.wac)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Current AMP:</span>
              <span className="ml-2 text-gray-900">
                {product.currentAmp ? formatCurrency(product.currentAmp) : 'Not calculated'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calculation Options */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Calculation Options</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={calculationOptions.applyMedicaidAdjustment}
                    onChange={(e) => setCalculationOptions(prev => ({
                      ...prev,
                      applyMedicaidAdjustment: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Medicaid Adjustment (23.1%)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={calculationOptions.apply340BAdjustment}
                    onChange={(e) => setCalculationOptions(prev => ({
                      ...prev,
                      apply340BAdjustment: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">340B Adjustment (25%)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={calculationOptions.applyPromptPayDiscount}
                    onChange={(e) => setCalculationOptions(prev => ({
                      ...prev,
                      applyPromptPayDiscount: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Prompt Pay Discount (2%)</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={calculationOptions.startDate}
                  onChange={(e) => setCalculationOptions(prev => ({
                    ...prev,
                    startDate: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={calculationOptions.endDate}
                  onChange={(e) => setCalculationOptions(prev => ({
                    ...prev,
                    endDate: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="pt-4 space-y-2">
                <button
                  onClick={handleCalculate}
                  disabled={calculationMutation.isPending}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  title="Calculate AMP"
                >
                  {calculationMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Calculator className="h-4 w-4 mr-2" />
                  )}
                  Calculate AMP
                </button>
                
                <button
                  onClick={handleGenerateReport}
                  disabled={reportMutation.isPending}
                  className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  title="Generate Report"
                >
                  {reportMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  Generate Report
                </button>
                
                <button
                  onClick={handleExport}
                  className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  title="Export CSV"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* AMP Calculation Result */}
          {calculationResult && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">AMP Calculation Result</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Key Metrics */}
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-600">Base AMP</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {formatCurrency(calculationResult.baseAMP)}
                          </p>
                        </div>
                        <DollarSign className="h-8 w-8 text-blue-600" />
                      </div>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-600">Adjusted AMP</p>
                          <p className="text-2xl font-bold text-green-900">
                            {formatCurrency(calculationResult.adjustedAMP)}
                          </p>
                        </div>
                        <TrendingUp className="h-8 w-8 text-green-600" />
                      </div>
                    </div>
                  </div>

                  {/* Compliance Status */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        {React.createElement(getComplianceIcon(calculationResult.compliance.medicaidCompliant), {
                          className: `h-5 w-5 ${getComplianceColor(calculationResult.compliance.medicaidCompliant)}`
                        })}
                        <span className="font-medium text-gray-900">Medicaid Compliance</span>
                      </div>
                      <p className={`text-sm ${getComplianceColor(calculationResult.compliance.medicaidCompliant)}`}>
                        {calculationResult.compliance.medicaidCompliant ? 'Compliant' : 'Non-Compliant'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        {React.createElement(getComplianceIcon(calculationResult.compliance.bestPriceCompliant), {
                          className: `h-5 w-5 ${getComplianceColor(calculationResult.compliance.bestPriceCompliant)}`
                        })}
                        <span className="font-medium text-gray-900">Best Price Compliance</span>
                      </div>
                      <p className={`text-sm ${getComplianceColor(calculationResult.compliance.bestPriceCompliant)}`}>
                        {calculationResult.compliance.bestPriceCompliant ? 'Compliant' : 'Non-Compliant'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Detailed Metrics */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">AMP to WAC Ratio</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatPercentage(calculationResult.metrics.ampToWACRatio * 100)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Medicaid Rebate</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(calculationResult.metrics.medicaidRebateAmount)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Best Price</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(calculationResult.metrics.bestPrice)}
                    </p>
                  </div>
                </div>

                {/* Issues */}
                {calculationResult.compliance.issues.length > 0 && (
                  <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
                    <h3 className="text-sm font-medium text-red-800 mb-2">Compliance Issues</h3>
                    <ul className="text-sm text-red-700 space-y-1">
                      {calculationResult.compliance.issues.map((issue, index) => (
                        <li key={index}>• {issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AMP Report */}
          {reportResult && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">AMP Report</h2>
              </div>
              <div className="p-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Report ID:</span>
                      <span className="ml-2 text-gray-900">{reportResult.reportId}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Generated:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(reportResult.generatedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Regulatory Information */}
                <div className="space-y-4">
                  <h3 className="text-md font-semibold text-gray-900">Regulatory Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-blue-600">Medicaid Rebate Amount</p>
                      <p className="text-xl font-bold text-blue-900">
                        {formatCurrency(reportResult.regulatoryInfo.medicaidRebateAmount)}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-green-600">Best Price</p>
                      <p className="text-xl font-bold text-green-900">
                        {formatCurrency(reportResult.regulatoryInfo.bestPrice)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                {reportResult.recommendations.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-md font-semibold text-gray-900 mb-4">Recommendations</h3>
                    <div className="space-y-3">
                      {reportResult.recommendations.map((rec, index) => (
                        <div key={index} className={`p-3 rounded-lg ${
                          rec.priority === 'critical' ? 'bg-red-50 border border-red-200' :
                          rec.priority === 'high' ? 'bg-yellow-50 border border-yellow-200' :
                          rec.priority === 'medium' ? 'bg-blue-50 border border-blue-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          <div className="flex items-start space-x-2">
                            {rec.priority === 'critical' && <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />}
                            {rec.priority === 'high' && <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />}
                            {rec.priority === 'medium' && <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5" />}
                            {rec.priority === 'low' && <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5" />}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{rec.message}</p>
                              <p className="text-xs text-gray-600 capitalize">{rec.priority} priority</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Calculation History */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Calculation History</h2>
            </div>
            <div className="p-6">
              {historyLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {historyData?.data?.history?.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(entry.amp)}
                        </p>
                        <p className="text-xs text-gray-600">
                          {new Date(entry.calculatedAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Base: {formatCurrency(entry.baseAMP)}</p>
                        <p className="text-xs text-gray-500 capitalize">{entry.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMPCalculation;
